import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { CoachingAttemptStatus, CoachingChannel } from '@euler/model/coaching';
import {
  CoachingAttemptEndReason,
  CoachingPracticeEnd,
} from '@euler/model/coaching-practice';
import { memo } from 'react';
import { Pressable, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { aiColors } from '../../../../../ai-suite/theme';

function closingLine(
  end: CoachingPracticeEnd,
  channel: CoachingChannel,
  customerName: string,
  exam: boolean,
  drill: boolean,
): string {
  if (end.status === CoachingAttemptStatus.Abandoned)
    return '还没开始就结束了，这次不计入';
  if (exam) return '已交卷';
  if (drill && end.endReason !== CoachingAttemptEndReason.Stopped)
    return '这一段练完了';
  if (end.endReason === CoachingAttemptEndReason.TurnLimit)
    return '本次回合已用完';
  if (channel === CoachingChannel.Phone) return '通话结束';
  if (channel === CoachingChannel.Wechat) return '对话结束';
  return end.endReason === CoachingAttemptEndReason.Stopped
    ? '练习结束'
    : `${customerName}离开了`;
}

/**
 * The short closing beat once the conversation is over, over the way on:
 * a bar the screen's full width, down through the home indicator.
 */
export const EndBeat = memo(
  ({
    end,
    channel,
    customerName,
    exam,
    drill,
    onNext,
  }: {
    end: CoachingPracticeEnd;
    channel: CoachingChannel;
    customerName: string;
    exam: boolean;
    drill: boolean;
    /** To the report, or back when nothing was said. */
    onNext: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const insets = useSafeAreaInsets();
    const abandoned = end.status === CoachingAttemptStatus.Abandoned;
    return (
      <View>
        <Typography.Text
          size={rpx(15)}
          color={aiColors.mutedForeground}
          style={{ textAlign: 'center', paddingVertical: rpx(14) }}
        >
          {closingLine(end, channel, customerName, exam, drill)}
        </Typography.Text>
        <Pressable
          accessibilityRole="button"
          onPress={onNext}
          style={({ pressed }) => ({
            alignItems: 'center',
            paddingTop: rpx(18),
            paddingBottom: insets.bottom + rpx(18),
            backgroundColor: aiColors.primary,
            opacity: pressed ? 0.85 : 1,
          })}
        >
          <Typography.Text
            size={rpx(17)}
            bold
            color={aiColors.primaryForeground}
          >
            {abandoned ? '返回' : '看报告'}
          </Typography.Text>
        </Pressable>
      </View>
    );
  },
);
